import { FormEvent, SetStateAction, useEffect } from "react"
import classNames from "classnames"

import { useAppSelector } from "store/hooks"
import {
  allContractorSubscriptionPlanOptions,
  contractorSubscriptionNewOptions,
  contractorSubscriptionPlanOptions,
} from "constants/SelectFieldOptions"
import { UITypes } from "types"
import SubscriptionPlan from "types/enums/SubscriptionPlan"

import ModalSectionTitle from "components/common/ModalSectionTitle/ModalSectionTitle"
import { InputNew } from "components/form/InputNew"
import { SelectStatic } from "components/form/SelectStatic"
import { SelectWithColor } from "components/form/SelectWithColor"
import { ToggleButton } from "components/form/ToggleButton"

import styles from "./index.module.scss"

interface Props {
  inputValues: UITypes.Contractor
  isCreate?: boolean
  onInputChange: (event: FormEvent<HTMLInputElement>) => void
  setInputValues: (value: SetStateAction<UITypes.Contractor>) => void
}

const SubscriptionSection = ({ inputValues, isCreate, onInputChange, setInputValues }: Props) => {
  const statusOptions = useAppSelector(state => state.tempData.contractorAccountStatusOptions) || []

  const currentPlan =
    allContractorSubscriptionPlanOptions.find(option => option.value === inputValues.subscriptionPlan) ||
    allContractorSubscriptionPlanOptions[0]

  const currentStatus = statusOptions.find(option => option.value === inputValues.accountStatus) || statusOptions[0]

  const handleChange = (values: UITypes.Option[]) => {
    if (!values.length) return

    const newPlan = values[0]?.value as SubscriptionPlan

    setInputValues(prevState => {
      const shouldEnableUniversity = newPlan === SubscriptionPlan.E_PLUS && !prevState.universityAccessible
      return {
        ...prevState,
        subscriptionPlan: newPlan,
        universityAccessible: shouldEnableUniversity ? true : prevState.universityAccessible,
      }
    })
  }

  const isEPlusPlan = inputValues.subscriptionPlan === SubscriptionPlan.E_PLUS

  useEffect(() => {
    if (isEPlusPlan && !inputValues.universityAccessible) {
      setInputValues(prevState => ({
        ...prevState,
        universityAccessible: true,
      }))
    }
  }, [isEPlusPlan, inputValues.universityAccessible])

  const handleAccountStatusChange = (values: UITypes.Option[]) => {
    if (!values.length) return

    setInputValues(prevState => {
      return { ...prevState, accountStatus: values[0]?.value as string }
    })
  }

  return (
    <div>
      <ModalSectionTitle title="Subscription plan" />
      <div className={classNames(styles.row, styles.subscriptionRow)}>
        <div className={styles.subscriptionPlanName}>
          <div>{currentPlan?.label}</div>
          <div>Monthly</div>
        </div>
        <div className={styles.subscriptionDropdownContainer}>
          <SelectStatic
            placeholder="Select subscription plan"
            values={[currentPlan]}
            options={isCreate ? contractorSubscriptionNewOptions : contractorSubscriptionPlanOptions}
            onChange={handleChange}
            contentRenderer={() => <>Change plan</>}
            backspaceDelete={false}
            menuClassName={styles.subscriptionSelectMenu}
            isSearchable={false}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={classNames(styles.toggleFieldWrapper, { [styles.disabled]: isEPlusPlan })}>
          <ToggleButton
            id="universityAccessible"
            name="universityAccessible"
            isChecked={inputValues.universityAccessible || false}
            onChange={isEPlusPlan ? undefined : onInputChange}
          >
            Contractor University
          </ToggleButton>
        </div>
      </div>
      <div className={styles.row}>
        <InputNew
          label="Stripe Customer ID"
          placeholder="Enter Stripe Customer ID"
          name="stripeCustomerId"
          value={inputValues.stripeCustomerId}
          className={styles.inputText}
          onChange={onInputChange}
        />
        <InputNew
          label="HubSpot Deal ID"
          placeholder="Enter HubSpot Deal ID"
          name="hubspotDealId"
          value={inputValues.hubspotDealId}
          className={styles.inputText}
          onChange={onInputChange}
        />
      </div>

      <div className={classNames(styles.row, styles.subscriptionRow)}>
        <div className={classNames(styles.modalLine, styles.subscriptionSectionModalLine)}></div>
      </div>
      <div className={styles.row}>
        {!isCreate && (
          <div className={styles.toggleFieldWrapper}>
            <ToggleButton id="isDisable" name="isDisable" isChecked={inputValues.isDisable} onChange={onInputChange}>
              Disable contractor
            </ToggleButton>
          </div>
        )}

        <SelectWithColor
          label="Contractor status"
          placeholder="Select contractor status"
          values={[currentStatus]}
          options={statusOptions}
          onChange={handleAccountStatusChange}
          isSearchable={false}
        />
      </div>
    </div>
  )
}

export default SubscriptionSection
